import type { ContractingFilters } from "@/types/domain";
import type { CompanyFilters } from "@/hooks/queries/useCompanies";

// Centralized query-key factories. Keeps keys consistent across queries,
// mutations and optimistic updates, and makes invalidation predictable.

export const contractingKeys = {
  all: ["contractings"] as const,
  lists: () => [...contractingKeys.all, "list"] as const,
  list: (filters: ContractingFilters) =>
    [...contractingKeys.lists(), filters] as const,
  details: () => [...contractingKeys.all, "detail"] as const,
  /** Los documentos de la propia empresa: no dependen de la licitación que se mire. */
  companyDocuments: () => [...contractingKeys.all, "company-documents"] as const,
  /** El análisis de calificación de la empresa, con el desglose por línea. */
  qualification: () => [...contractingKeys.all, "qualification"] as const,
  detail: (id?: number, searchProfileId?: number) =>
    [...contractingKeys.details(), id, searchProfileId] as const,
};

export const suretyKeys = {
  all: ["surety"] as const,
  panel: (month?: string) => [...suretyKeys.all, "panel", month ?? "current"] as const,
  market: (month?: string) => [...suretyKeys.all, "market", month ?? "current"] as const,
  clients: () => [...suretyKeys.all, "clients"] as const,
};

export const dashboardKeys = {
  all: ["dashboard"] as const,
  data: (filters: ContractingFilters) =>
    [...dashboardKeys.all, filters] as const,
};

export const workAdvisoryKeys = {
  all: ["work-advisory"] as const,
  // Sin id es "el último publicado": su propia entrada de caché.
  report: (reportId?: number) =>
    [...workAdvisoryKeys.all, "report", reportId ?? "latest"] as const,
  history: () => [...workAdvisoryKeys.all, "history"] as const,
  reviewList: () => [...workAdvisoryKeys.all, "review"] as const,
  competition: (contractingId: number) =>
    [...workAdvisoryKeys.all, "competition", contractingId] as const,
  preparation: (reportId: number) =>
    [...workAdvisoryKeys.all, "preparation", reportId] as const,
  bases: (contractingId: number) =>
    [...workAdvisoryKeys.all, "bases", contractingId] as const,
  analysis: (contractingId: number, organizationId: string) =>
    [...workAdvisoryKeys.all, "analysis", contractingId, organizationId] as const,
  companyDocuments: (organizationId?: string) =>
    [...workAdvisoryKeys.all, "company-documents", organizationId] as const,
  review: (reportId?: number) =>
    [...workAdvisoryKeys.all, "review", reportId] as const,
};

export const statusCountsKeys = {
  all: ["status-counts"] as const,
};

export const searchProfileKeys = {
  all: ["search-profiles"] as const,
  // Las líneas de la empresa con sus asignados (pantalla de administración).
  organization: () => ["search-profiles", "organization"] as const,
};

export const teamKeys = {
  all: ["team"] as const,
  // Todas las variantes (equipo o mías, N meses), para refrescarlas juntas.
  monthlyStatsAll: () => ["team", "monthly-stats"] as const,
  monthlyStats: (scope: string, months: number) =>
    [...teamKeys.monthlyStatsAll(), scope, months] as const,
};

export const contractingActivityKeys = {
  feed: (scope: string) => ["contracting-activity", "feed", scope] as const,
  all: ["contracting-activity"] as const,
  timeline: (contractingId?: number) =>
    [...contractingActivityKeys.all, contractingId] as const,
};

export const contractingNoteKeys = {
  all: ["contracting-notes"] as const,
  byContracting: (contractingId?: number) =>
    [...contractingNoteKeys.all, contractingId] as const,
};

export const contractingTaskKeys = {
  all: ["contracting-tasks"] as const,
  list: (filters: { contractingId?: number; scope?: string; includeDone?: boolean }) =>
    [...contractingTaskKeys.all, filters] as const,
};

export const interestKeys = {
  all: ["interests"] as const,
  byProfile: (searchProfileId?: number) =>
    [...interestKeys.all, searchProfileId] as const,
  categories: ["interest-categories"] as const,
};

export const profileItemKeys = {
  all: ["profile-items"] as const,
  byProfile: (searchProfileId?: number) =>
    [...profileItemKeys.all, searchProfileId] as const,
};

export const locationKeys = {
  departments: ["departments"] as const,
  provinces: (departmentId?: number) => ["provinces", departmentId] as const,
  districts: (provinceId?: number) => ["districts", provinceId] as const,
};

export const procuringEntityKeys = {
  search: (search: string) => ["procuring-entities", search] as const,
};

// Todas las keys cuelgan de `all` para que invalidar `companyKeys.all` (p. ej.
// al desbloquear una licitación) alcance detalle, contratos, ítems y red.
export const companyKeys = {
  all: ["companies"] as const,
  list: (filters: CompanyFilters & { page: number; size: number }) =>
    [...companyKeys.all, "list", filters] as const,
  detail: (companyId?: number) =>
    [...companyKeys.all, "detail", companyId] as const,
  contractsAll: () => [...companyKeys.all, "contracts"] as const,
  contracts: (companyId?: number, keywords?: string[]) =>
    [...companyKeys.contractsAll(), companyId, keywords] as const,
  network: (companyId?: number) =>
    [...companyKeys.all, "network", companyId] as const,
  priceItems: (companyId?: number) =>
    [...companyKeys.all, "price-items", companyId] as const,
  followed: () => [...companyKeys.all, "followed"] as const,
};

export const betaKeys = {
  info: ["beta-information"] as const,
};

export const whatsappKeys = {
  status: ["whatsapp-status"] as const,
};

export const viewQuotaKeys = {
  all: ["view-quota"] as const,
};

export const onboardingKeys = {
  all: ["onboarding"] as const,
  supplierPreview: (terms: string[], departmentIds: number[], details: boolean) =>
    [...onboardingKeys.all, "supplier-preview", terms, departmentIds, details] as const,
  contractorPreview: (
    subjects: string[],
    terms: string[],
    departmentIds: number[],
    amountMin: number | null,
    amountMax: number | null,
    details: boolean,
  ) =>
    [
      ...onboardingKeys.all,
      "contractor-preview",
      subjects,
      terms,
      departmentIds,
      amountMin,
      amountMax,
      details,
    ] as const,
  documents: () => [...onboardingKeys.all, "documents"] as const,
  qualification: () => [...onboardingKeys.all, "qualification"] as const,
};

export const billingKeys = {
  all: ["billing"] as const,
  plans: () => [...billingKeys.all, "plans"] as const,
  subscription: () => [...billingKeys.all, "subscription"] as const,
  payments: () => [...billingKeys.all, "payments"] as const,
};

export const adminKeys = {
  users: ["admin-users"] as const,
  organizations: ["admin-organizations"] as const,
  plans: ["admin-plans"] as const,
  metrics: (days: number) => ["admin-metrics", days] as const,
  contractingErrors: (filters?: unknown) =>
    ["admin-contracting-errors", filters ?? null] as const,
  contractingMissingFiles: (filters?: unknown) =>
    ["admin-contracting-missing-files", filters ?? null] as const,
  bidDocuments: (filters?: unknown) =>
    ["admin-bid-documents", filters ?? null] as const,
};

export const notificationKeys = {
  list: ["notifications"] as const,
  unreadCount: ["notifications", "unread-count"] as const,
};
